/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/AIAssistantRightRail.tsx
 * PURPOSE: Perplexity-style right assistant rail — in-flow split on desktop, full overlay on mobile.
 * DEPENDENCIES: AiAssistantPanel, useShellAssistant, useShellAssistantRailWidth
 * PUBLIC_API: AIAssistantRightRail
 * AGENT_SAFETY: RESTRICTED
 * RECENT: 2026-06-18 [2.2.1450] — Desktop in-flow resizable rail; mobile overlay + backdrop; lazy **`AiAssistantPanel`** **`variant="page"`** with **`railEmbedded`**.
 */

import { lazy, Suspense, useCallback, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useMobileShellActive } from '../../hooks/useMobileShellActive';
import { SHELL_DRAWER_BACKDROP_CLASS } from '../shellDrawerLayout';
import type { AssistantDockRouteContext } from './ShellAssistantContext';
import { useShellAssistant } from './ShellAssistantContext';
import { mergeLiveDockPageContext } from './shellAssistantDockContext';
import { useShellAssistantRailWidth } from './useShellAssistantRailWidth';
import { inferShellModuleFromPath } from '@shared/lib/frontend/utils/assistantRouteFocusHint';
import {
  AssistantRunInBrowserButton,
  isBrowserCapabilityEnabled,
  useBrowserCapability,
} from '@platform/browser/frontend';

const AiAssistantPanel = lazy(() =>
  import('../../design-system/components/AiAssistant/AiAssistantPanel').then((m) => ({
    default: m.AiAssistantPanel,
  }))
);

function mergeRouteDisplayContext(
  base: AssistantDockRouteContext,
  override?: Partial<AssistantDockRouteContext>
): AssistantDockRouteContext {
  if (!override) return base;
  return {
    route: override.route !== undefined ? override.route : base.route,
    selectedRecord:
      override.selectedRecord !== undefined ? override.selectedRecord : base.selectedRecord,
    activeFilters:
      override.activeFilters !== undefined ? override.activeFilters : base.activeFilters,
  };
}

export type AIAssistantRightRailProps = {
  pageContext: { path: string; title?: string };
  context?: Partial<AssistantDockRouteContext>;
  hidden?: boolean;
};

function AssistantPanelBody({
  effectivePageContext,
  mergedRouteContext,
  onClose,
  composerTopSlot,
  browserAutomationEnabled,
  session,
}: {
  effectivePageContext: { path: string; title?: string };
  mergedRouteContext: AssistantDockRouteContext;
  onClose: () => void;
  composerTopSlot: ReactNode;
  browserAutomationEnabled: boolean;
  session: ReturnType<typeof useShellAssistant>['session'];
}) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full min-h-[8rem] items-center justify-center p-4 text-sm text-gray-500 dark:text-gray-400">
          Loading assistant…
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <AiAssistantPanel
          variant="page"
          railEmbedded
          isOpen
          onClose={onClose}
          pageContext={effectivePageContext}
          prefillContext={session.prefillContext || null}
          prefillAutoSend={session.prefillAutoSend}
          suggestedPrompts={session.suggestedPrompts}
          prefillNonce={session.prefillNonce}
          subscribeGlobalPrefill
          elevatedStack={false}
          embedPortalRoot={null}
          composerTopSlot={composerTopSlot}
          browserAutomationEnabled={browserAutomationEnabled}
          shellRouteContext={{
            route: mergedRouteContext.route,
            selectedRecord: mergedRouteContext.selectedRecord,
            activeFilters: mergedRouteContext.activeFilters,
          }}
        />
      </div>
    </Suspense>
  );
}

export function AIAssistantRightRail({ pageContext, context, hidden }: AIAssistantRightRailProps) {
  const isMobile = useMobileShellActive();
  const location = useLocation();
  const { session, collapseDock, routeMeta } = useShellAssistant();
  const { data: browserCapability, isLoading: browserCapabilityLoading } = useBrowserCapability();
  const browserAutomationEnabled = isBrowserCapabilityEnabled(
    browserCapability,
    browserCapabilityLoading
  );
  const {
    railWidthPx,
    onResizePointerDown,
    onResizePointerMove,
    onResizePointerEnd,
  } = useShellAssistantRailWidth(isMobile);

  const baseRouteContext = useMemo(
    (): AssistantDockRouteContext => ({
      route: routeMeta.route || `${location.pathname}${location.search}`,
      selectedRecord: routeMeta.selectedRecord ?? null,
      activeFilters: routeMeta.activeFilters ?? null,
    }),
    [routeMeta.route, routeMeta.selectedRecord, routeMeta.activeFilters, location.pathname, location.search]
  );

  const mergedRouteContext = useMemo(
    () => mergeRouteDisplayContext(baseRouteContext, context),
    [baseRouteContext, context]
  );

  const expanded = session.dockExpanded && session.panelOpen;

  const effectivePageContext = useMemo(
    () => mergeLiveDockPageContext(pageContext, session.pageContext),
    [pageContext, session.pageContext]
  );

  const onRailClose = useCallback(() => {
    collapseDock();
  }, [collapseDock]);

  const composerTopSlot = useMemo(() => {
    if (!browserAutomationEnabled) return null;
    return (
      <AssistantRunInBrowserButton
        compact
        prefillContext={session.prefillContext}
        pagePath={effectivePageContext.path}
        pageTitle={effectivePageContext.title}
        suggestedPrompts={session.suggestedPrompts}
        onBeforeNavigate={collapseDock}
        dataTestId="shell-assistant-run-in-browser"
      />
    );
  }, [
    browserAutomationEnabled,
    collapseDock,
    effectivePageContext.path,
    effectivePageContext.title,
    session.prefillContext,
    session.suggestedPrompts,
  ]);

  if (hidden || !expanded) return null;

  const contextSnapshot = (
    <span hidden data-testid="shell-assistant-context-snapshot">
      {JSON.stringify({
        route: mergedRouteContext.route,
        selectedRecord: mergedRouteContext.selectedRecord,
        activeFilters: mergedRouteContext.activeFilters,
        page_path: effectivePageContext.path,
        module: inferShellModuleFromPath(effectivePageContext.path),
      })}
    </span>
  );

  if (isMobile) {
    if (typeof document === 'undefined') return null;
    return createPortal(
      <>
        <div
          data-testid="ai-panel-backdrop"
          className={SHELL_DRAWER_BACKDROP_CLASS}
          aria-hidden={false}
          onClick={onRailClose}
        />
        <aside
          className="fixed right-0 top-[var(--shell-topbar-height,4.25rem)] bottom-0 z-[110] flex w-full max-w-md flex-col overflow-hidden border-l border-gray-200 bg-gray-50 shadow-2xl dark:border-gray-700 dark:bg-gray-900"
          data-testid="shell-ai-assistant-dock"
          data-assistant-frame="true"
          role="region"
          aria-label="AI Assistant panel"
        >
          {contextSnapshot}
          <AssistantPanelBody
            effectivePageContext={effectivePageContext}
            mergedRouteContext={mergedRouteContext}
            onClose={onRailClose}
            composerTopSlot={composerTopSlot}
            browserAutomationEnabled={browserAutomationEnabled}
            session={session}
          />
        </aside>
      </>,
      document.body
    );
  }

  return (
    <aside
      className="relative z-20 flex h-full shrink-0 flex-col overflow-hidden border-l border-gray-200/80 bg-gray-50 shadow-[-8px_0_32px_rgba(15,23,42,0.06)] dark:border-gray-700/60 dark:bg-gray-900 dark:shadow-[-8px_0_32px_rgba(0,0,0,0.35)]"
      data-testid="shell-ai-assistant-dock"
      data-assistant-frame="true"
      role="region"
      aria-label="AI Assistant panel"
      style={{ width: railWidthPx, minWidth: railWidthPx, maxWidth: railWidthPx }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize assistant panel"
        data-testid="shell-ai-assistant-rail-resize"
        className="group absolute bottom-0 left-0 top-0 z-10 flex w-1.5 shrink-0 cursor-ew-resize items-center justify-center touch-none"
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerEnd}
        onPointerCancel={onResizePointerEnd}
      >
        <span
          className="pointer-events-none h-12 w-1 rounded-full bg-gray-300/80 transition-all duration-200 group-hover:h-16 group-hover:bg-gray-400/90 dark:bg-gray-600/80 dark:group-hover:bg-gray-500/90"
          aria-hidden
        />
      </div>
      <button
        type="button"
        className="absolute left-2 top-2 z-20 hidden"
        aria-hidden
        tabIndex={-1}
        onClick={onRailClose}
      />
      {contextSnapshot}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden pl-1.5">
        <AssistantPanelBody
          effectivePageContext={effectivePageContext}
          mergedRouteContext={mergedRouteContext}
          onClose={onRailClose}
          composerTopSlot={composerTopSlot}
          browserAutomationEnabled={browserAutomationEnabled}
          session={session}
        />
      </div>
    </aside>
  );
}
